import axios from "axios";
import { useEffect, useState } from "react";
import { getKidsSoundOn } from "@/hooks/useKidsSound";

// One shared Lumi voice player for every Kids Corner activity: follows the Kids Corner Sound toggle,
// never overlaps (a new phrase stops the previous one) and remembers the last phrase for Replay.
const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const memo = new Map(); // text -> { b64, fmt } so Replay never re-requests the same speech
const listeners = new Set();
let current = null;
let lastText = null;
let currentDone = null; // resolves the in-flight speakLumi promise when audio ends OR is stopped
let activeStop = null; // whoever is making sound right now (Lumi or a video) — only one at a time

// Call before starting any sound. Stops whatever else was playing so narration, music and video never overlap.
export const claimPlayback = (stop) => {
  if (activeStop && activeStop !== stop) { try { activeStop(); } catch { /* ignore */ } }
  activeStop = stop;
};
export const releasePlayback = (stop) => { if (activeStop === stop) activeStop = null; };

const notify = () => listeners.forEach((fn) => fn({ speaking: !!current, lastText }));

// One reusable <audio> element. Safari / iOS only let a page start sound from inside a tap or click, and Lumi's
// feedback audio can arrive a few seconds after the tap. Playing a silent clip on the element during the first
// tap "unlocks" it, so later feedback on the same element is allowed to play.
const SILENT_WAV = "data:audio/wav;base64,UklGRiQAAABXQVZFZm10IBAAAAABAAEARKwAAIhYAQACABAAZGF0YQAAAAA=";
let player = null;
let unlocked = false;
const getPlayer = () => {
  if (!player && typeof Audio !== "undefined") { player = new Audio(); player.preload = "auto"; }
  return player;
};
const unlock = () => {
  if (unlocked) return;
  const a = getPlayer();
  if (!a || current) return;
  unlocked = true;
  try {
    a.muted = true;
    a.src = SILENT_WAV;
    const p = a.play();
    const reset = () => { if (!current) { try { a.pause(); } catch { /* ignore */ } } a.muted = false; };
    if (p && p.then) p.then(reset).catch(() => { a.muted = false; unlocked = false; });
    else reset();
  } catch { a.muted = false; unlocked = false; }
};
if (typeof window !== "undefined") {
  ["pointerdown", "touchend", "keydown"].forEach((ev) => window.addEventListener(ev, unlock, { capture: true, passive: true }));
}

export const stopLumiAudio = () => {
  if (current) {
    try { current.pause(); } catch { /* ignore */ }
    current = null;
    notify();
    if (currentDone) { const d = currentDone; currentDone = null; d(); }
  }
};

const dataUrl = ({ b64, fmt }) => `data:audio/${fmt === "mp3" ? "mpeg" : "wav"};base64,${b64}`;

const playEntry = (entry, claim) =>
  new Promise((resolve) => {
    stopLumiAudio();
    claimPlayback(claim || stopLumiAudio); // `claim` lets a caller pair Lumi's voice with its own media (e.g. Meet Lumi)
    const a = getPlayer() || new Audio();
    a.muted = false;
    a.src = dataUrl(entry);
    current = a;
    notify();
    const done = () => { if (current === a) { current = null; notify(); } if (currentDone === done) currentDone = null; resolve(true); };
    currentDone = done;
    a.onended = done;
    a.onerror = done;
    a.play().catch(done);
  });

// Play audio the server already returned with a result (e.g. sentence-check), remembering it for Replay.
export const playLumiBase64 = async (text, b64, fmt = "wav") => {
  if (!text || !b64) return false;
  const entry = { b64, fmt: fmt || "wav" };
  memo.set(text, entry);
  lastText = text;
  if (!getKidsSoundOn()) { notify(); return false; }
  return playEntry(entry);
};

export const speakLumi = async (text, { claim } = {}) => {
  const phrase = (text || "").trim();
  if (!phrase) return false;
  lastText = phrase;
  notify();
  if (!getKidsSoundOn()) return false;
  let entry = memo.get(phrase);
  if (!entry) {
    stopLumiAudio();
    try {
      const res = await axios.post(`${API}/kids/lumi-speak`, { text: phrase }, { timeout: 20000 });
      if (!res.data?.audio_base64) return false;
      entry = { b64: res.data.audio_base64, fmt: res.data.format || "wav" };
      memo.set(phrase, entry);
    } catch {
      return false;
    }
  }
  if (!getKidsSoundOn() || lastText !== phrase) return false; // sound turned off or something newer was requested
  return playEntry(entry, claim);
};

export const replayLumi = () => (lastText ? speakLumi(lastText) : Promise.resolve(false));
export const clearLumiReplay = () => { lastText = null; notify(); };

export const useLumiAudioState = () => {
  const [state, setState] = useState({ speaking: !!current, lastText });
  useEffect(() => {
    listeners.add(setState);
    return () => listeners.delete(setState);
  }, []);
  return state;
};

if (typeof window !== "undefined") {
  window.addEventListener("kids-sound-change", () => { if (!getKidsSoundOn()) stopLumiAudio(); });
}
